/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IAddressSelectItem, IAddressSelectProps } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { ICheckoutShippingAddressViewProps } from '@msdyn365-commerce-modules/address';
import {
    ICheckoutShippingAddressConfig,
    ICheckoutShippingAddressProps
} from '../definition-extensions/checkout-shipping-address.ext.props.autogenerated';
// import AdressDropdownComponent from './address-dropdown';

interface IAddressAddUpdateExtProps extends IAddressAddUpdateProps {
    shipUsaOnlyMessageText: string;
    allowInternationalShipping: boolean;
}

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address Select Component.
 * @param param0 - Root param.
 * @param param0.SelectAddress - Select address.
 * @param param0.addButton - Add button.
 * @param param0.items - IAddressSelectItem[].
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - SelectAddress Node.
 */
const AddressSelect: React.FC<IAddressSelectProps> = ({
    SelectAddress,
    addButton,
    items,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...SelectAddress}>
            {addButton}
            {items.map((item: IAddressSelectItem) => {
                const SelectItem = item.SelectItem;
                return (
                    <Node {...SelectItem} key={item.key}>
                        {item.input}
                        <AddressShow {...item.showItems} />
                    </Node>
                );
            })}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateExtProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    shipUsaOnlyMessageText,
    allowInternationalShipping
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {!allowInternationalShipping && renderDeliveryLimitationMessage(shipUsaOnlyMessageText)}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                // if the address item is the country input, do not render.
                if (!allowInternationalShipping) {
                    if (key === 'ThreeLetterISORegionName') return <></>;
                } else {
                    // filter to only USA and Canada
                    if (key === 'ThreeLetterISORegionName') {
                        if (React.isValidElement(input)) {
                            const filteredCountries = [
                                { key: 'CAN', value: 'Canada' },
                                { key: 'USA', value: 'United States' }
                            ];
                            const clonedInput = React.cloneElement(input, { ...input.props, displayData: filteredCountries });
                            return (
                                <Node {...AddressItem} key={key}>
                                    {label}
                                    {alert}
                                    {clonedInput}
                                </Node>
                            );
                        }
                    }
                }
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const renderDeliveryLimitationMessage = (shipUsaOnlyMessageText: string) => (
    <div className={'ms-checkout-shipping-address__delivery-limitation'}>
        <div className={'ms-checkout-shipping-address__delivery-limitation-message'}>
            <span className='msi-information' />
            {shipUsaOnlyMessageText}
        </div>
    </div>
);

/**
 * Checkout Shipping Address View Component.
 * @param props - Props.
 * @returns - CheckoutShippingAddress Module.
 */
const CheckoutShippingAddressView: React.FC<ICheckoutShippingAddressViewProps &
    ICheckoutShippingAddressProps<ICheckoutShippingAddressConfig>> = props => {
    const {
        CheckoutShippingAddress,
        checkoutErrorRef,
        alert,
        viewState,
        showAddress,
        showAddressSelect,
        showAddOrUpdateAddress,
        cartLineImages,
        resources,
        config
    } = props;

    return (
        <Module {...CheckoutShippingAddress} ref={checkoutErrorRef}>
            {alert}
            {cartLineImages}
            {viewState.isShowAddress && <AddressShow {...showAddress} />}
            {viewState.isShowAddresList && <AddressSelect {...showAddressSelect} />}
            {viewState.isShowAddOrUpdateAddress && (
                <AddressAddUpdate
                    {...showAddOrUpdateAddress}
                    shipUsaOnlyMessageText={resources.shipUsaOnlyMessageText}
                    allowInternationalShipping={config.allowInternationalShipping!}
                />
            )}
        </Module>
    );
};

export default CheckoutShippingAddressView;
